import React, { SyntheticEvent, useMemo } from "react"
import styled from "styled-components"
import { SidebarTab, useUi } from "../../hooks/context/ui"
import useMapData from "../../hooks/queries/use-map-data"
import useTrackedPoints from "../../hooks/queries/tracked-points"
import AdBanner from "../AdBanner"
import Search from "./Search"
import { CDN_URL } from "../../config/games"

const intersect = (arr1: unknown[], arr2: unknown[]) => {
  return arr1.filter((x) => arr2.includes(x))
}

const Sidebar = ({ onDisplayCategories, setOnDisplayCategories, staticDataUrl }: any) => {
  const { categories, categoryMap, groups, isPending } = useMapData(staticDataUrl)
  const { sidebarTab, isShowProgress, setIsShowProgress } = useUi()
  const { trackedPointIds } = useTrackedPoints()

  // get the number of tracked points in each category
  const categoryIdTrackedPointCountsMap = useMemo(() => {
    const map: { [categoryId: string]: number } = {}
    ;(categories || []).forEach((category) => {
      const cat = categoryMap?.[category.id]
      map[category.id] = cat?.pointIds ? intersect(cat.pointIds, trackedPointIds).length : 0
    })
    return map
  }, [isPending, trackedPointIds])

  const handleCategoryButtonClick = (e: SyntheticEvent, categoryId: string) => {
    e.preventDefault()
    if (e.type === "click") {
      if (!onDisplayCategories) {
        return setOnDisplayCategories((categories || []).filter((c) => c.id !== categoryId).map((c) => c.id))
      }
      setOnDisplayCategories(
        onDisplayCategories.includes(categoryId)
          ? onDisplayCategories.filter((p: string) => p !== categoryId)
          : [...onDisplayCategories, categoryId],
      )
    } else if (e.type === "contextmenu") {
      setOnDisplayCategories([categoryId])
    }
  }

  const CategoriesTab = (
    <>
      {/*<div>Elden Ring Maps</div>*/}
      <div style={{ display: "flex", flexDirection: "row", borderBottom: "1px solid #555555" }}>
        <CategoryButton isActive onClick={() => setOnDisplayCategories(null)} noWidth>
          Show all
        </CategoryButton>
        <CategoryButton isActive onClick={() => setOnDisplayCategories([])} noWidth>
          Hide all
        </CategoryButton>
        <CategoryButton isActive={isShowProgress} onClick={() => setIsShowProgress(!isShowProgress)} noWidth>
          Show Progress
        </CategoryButton>
      </div>
      <Scroll sidebarTab={sidebarTab} className="scrollbar-2">
        {groups?.map((group) => (
          <div key={group.id}>
            <GroupTitle
              onClick={() => {
                if (!onDisplayCategories) {
                  // all selected
                  return setOnDisplayCategories(
                    (categories || []).filter(({ groupId }) => groupId !== group.id).map(({ shortName }) => shortName),
                  )
                }
                // if all point types are selected, then remove all point types
                if (group.categories.every(({ id }) => onDisplayCategories.includes(id))) {
                  setOnDisplayCategories(
                    onDisplayCategories.filter((id: number) => !group.categories.map((c) => c.id).includes(id)),
                  )
                } else {
                  setOnDisplayCategories([
                    ...onDisplayCategories.filter(
                      (pointType: string) => !group.categories.map((c) => c.id).includes(pointType),
                    ),
                    ...group.categories.map((c) => c.id),
                  ])
                }
              }}
            >
              {group.name}
            </GroupTitle>
            <ControlGroup onContextMenu={(e) => e.preventDefault()}>
              {group.categories.map(({ id, shortName, name, pointCount }) => (
                <CategoryButton
                  key={id}
                  onClick={(e) => {
                    handleCategoryButtonClick(e, id)
                  }}
                  onContextMenu={(e) => {
                    handleCategoryButtonClick(e, id)
                  }}
                  isActive={!onDisplayCategories || onDisplayCategories.includes(id)}
                  isShowProgress={isShowProgress}
                  percentage={Math.floor((categoryIdTrackedPointCountsMap[id] / pointCount) * 100)}
                >
                  <div className="progress" />
                  <span>
                    <SvgCategoryIcon
                      url={`${CDN_URL}/assets/category-svg/${shortName}.svg`}
                      isActive={!onDisplayCategories || onDisplayCategories.includes(id)}
                    />
                    {name}
                  </span>
                  {isShowProgress && (
                    <span className="count">
                      {categoryIdTrackedPointCountsMap[id]} / {pointCount}
                    </span>
                  )}
                  {!isShowProgress && <span>{pointCount}</span>}
                </CategoryButton>
              ))}
            </ControlGroup>
          </div>
        ))}
      </Scroll>
    </>
  )

  // return useMemo(
  // () => (
  return (
    <Root sidebarTab={sidebarTab}>
      <Content sidebarTab={sidebarTab} isShowProgress={isShowProgress}>
        {sidebarTab === "categories" && CategoriesTab}
        {sidebarTab === "search" && <Search staticDataUrl={staticDataUrl} />}
        <AdBanner adSlot="" adFormat="rectangle" style={{ marginTop: '16px' }} />
      </Content>
    </Root>
  )
  // [onDisplayCategories, groups, sidebarTab, isShowProgress, categoryIdTrackedPointCountsMap],
  // )
}

export default Sidebar

export const SIDEBAR_WIDTH = 400
export const SIDEBAR_WIDTH_WIDE = 460

const CATEGORY_BUTTON_WIDTH = 190
const CATEGORY_BUTTON_WIDTH_WIDE = 220

const Root = styled.div<{ sidebarTab: string | false }>`
  position: fixed;
  top: 50px;
  left: 10px;
  z-index: 20;
  // visibility: ${({ sidebarTab }) => (sidebarTab ? "visible" : "hidden")};
  //left: ${({ sidebarTab }) => (sidebarTab ? "0" : `${-SIDEBAR_WIDTH - 20}px`)};
  transition: all 0.3s;
  //width: 300px;
  height: ${({ sidebarTab }) => (sidebarTab ? "90%" : "0px")};
  color: white;
  display: flex;
  flex-direction: row-reverse;
  // https://stackoverflow.com/questions/7347532/how-to-position-a-div-scrollbar-on-the-left-hand-side
  direction: rtl;
`

const ControlGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const Scroll = styled.div<{ sidebarTab: SidebarTab }>`
  overflow-y: ${({ sidebarTab }) => (sidebarTab === "categories" ? "auto" : "unset")};
  height: 100%;
  // https://stackoverflow.com/questions/7347532/how-to-position-a-div-scrollbar-on-the-left-hand-side
  * {
    direction: ltr;
  }
`

const Content = styled.div<{ sidebarTab: SidebarTab; isShowProgress: boolean }>`
  transition: height 0.3s;
  position: relative;
  //background-color: rgb(0, 0, 0);
  background-color: rgba(29, 29, 29, 0.92);
  padding: ${({ sidebarTab }) => (sidebarTab ? "10px" : "0")} 5px;
  width: ${({ isShowProgress }) => (isShowProgress ? SIDEBAR_WIDTH_WIDE : SIDEBAR_WIDTH)}px;
  direction: ltr;
`

const CategoryButton = styled.button<{
  isActive?: boolean
  isShowProgress?: boolean
  percentage?: number
  noWidth?: boolean
}>`
  position: relative;
  text-transform: uppercase;
  //font-family: "Electrolize";
  cursor: pointer;
  background: none;
  border: none;
  //border-radius: 5px;
  // border: 1px solid ${({ isActive }) => (isActive ? "white" : "rgba(255,255,255,.5)")};
  width: ${({ noWidth, isShowProgress }) =>
    noWidth ? "inherit" : (isShowProgress ? CATEGORY_BUTTON_WIDTH_WIDE : CATEGORY_BUTTON_WIDTH) + "px"};
  margin: 4px 0;
  margin-left: 0;
  color: ${({ isActive }) => (isActive ? "white" : "rgba(255,255,255,.5)")};
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0 5px;

  :hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  div {
    text-align: left;
    vertical-align: middle;
  }

  img {
    width: 20px;
    height: 30px;
  }

  span {
    white-space: nowrap;
  }

  .count {
    margin-left: auto;
  }

  .progress {
    opacity: ${({ isShowProgress }) => (isShowProgress ? 1 : 0)};
    transition: all 0.3s;
    position: absolute;
    bottom: -5px;
    left: 5px;
    width: ${({ percentage }) => percentage}%;
    height: 3px;
    background-image: linear-gradient(
      to right,
      ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.colors.purple}
    );
  }
`

const GroupTitle = styled.h3`
  margin-top: 10px;
  margin-bottom: 5px;
  cursor: pointer;
  padding-left: 5px;
`

export const SvgCategoryIcon = styled.div<{ url: string; color?: string; isActive?: boolean }>`
  display: inline-block;
  background-color: ${({ color, isActive }) => color || (isActive ? "white" : "rgba(255,255,255,.5)")};
  -webkit-mask: url(${({ url }) => url}) no-repeat center;
  -webkit-mask-size: contain;
  mask: url(${({ url }) => url}) no-repeat center;
  mask-size: contain;
  width: 18px;
  height: 18px;
  margin-right: 5px;
  flex-shrink: 0;
`
